<template>
  <div class="promotion-details-header space-y-4" :style="cardStyle">
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex items-center gap-2">
        <span
          v-if="promotion.type?.name"
          class="inline-flex rounded-md px-1 py-1 text-xs font-medium text-[color:var(--promotion-color)] sm:px-3"
          :style="{ backgroundColor: 'var(--promotion-color-badge)' }"
        >
          {{ promotion.type.name }}
        </span>

        <span
          v-if="statusLabel"
          class="inline-flex rounded-md px-1 py-1 text-xs font-medium sm:px-3"
          :class="statusBadgeClass"
        >
          {{ statusLabel }}
        </span>
      </div>

      <div class="ml-4 flex items-center gap-1.5 text-xs text-gray-400 md:text-sm">
        <OurSvgIcon class="shrink-0" :src="icons.Event" size="xs" color="gray" variant="light" />
        <span>{{ dateLabel }}</span>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:items-start min-[1400px]:grid-cols-[2fr_1.3fr]">
      <div
        class="promotion-details-header__banner overflow-hidden rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
        :class="{ 'bg-gray-100': !bannerUrl }"
      >
        <picture v-if="bannerUrl" class="promotion-details-header__banner-image">
          <img :src="bannerUrl" :alt="promotion.name" />
        </picture>
        <div v-else class="flex items-center justify-center px-6 py-16">
          <OurSvgIcon :src="icons.Image" size="2xl" color="gray" variant="dark" />
        </div>
      </div>

      <div class="flex min-w-0 flex-col gap-4">
        <div
          class="flex min-w-0 items-center gap-3 rounded-xl bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)] sm:gap-4 md:p-6"
        >
          <PromotionHexIcon
            class="shrink-0"
            :src="iconUrl"
            :alt="promotion.name"
            :color="accentColor"
            :size="64"
          />

          <div class="min-w-0 flex-1 self-stretch">
            <div
              v-if="isUserDealer && !progressData.hasStarted"
              class="flex h-full items-center text-sm text-[#5c6378]"
            >
              {{ $t("dealerPromotions.notStarted") }}
            </div>

            <PromotionProgress
              v-else
              :variant="isUserDealer ? 'dealer' : 'conditions'"
              :sum="progressData.shipmentsSum"
              :target-value="progressTargetValue"
              :level="progressData.level"
              :next-level="progressData.nextLevel"
              :color="accentColor"
              :measurement-unit="promotion.measurementUnit || ''"
            />
          </div>
        </div>

        <div
          v-if="rewardText"
          class="flex items-center gap-3 rounded-xl bg-[color:var(--promotion-color-badge)] p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)] md:p-6"
        >
          <OurSvgIcon
            class="shrink-0 text-gray-400"
            :src="icons.Gift"
            size="lg"
            color="gray"
            variant="light"
            aria-hidden="true"
          />
          <div class="flex min-w-0 flex-1 flex-col gap-1">
            <span class="text-xs font-medium text-[#5c6378]">{{
              $t("dealerPromotions.reward")
            }}</span>
            <span class="text-sm font-bold leading-snug text-[#1e204d]">{{ rewardText }}</span>
          </div>
          <span
            v-if="isUserDealer"
            class="shrink-0 rounded-full bg-[color:var(--promotion-color-light)] px-2.5 py-0.5 text-base font-bold text-[color:var(--promotion-color)]"
          >
            {{ multiplierLabel }}
          </span>
        </div>
      </div>
      <div
        v-if="showDescription && promotion.description"
        class="col-span-1 rounded-xl bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)] md:p-6 lg:col-span-2"
      >
        <h2 class="mb-3 text-base font-bold text-[#1e204d]">
          {{ $t("dealerPromotions.descriptionTitle") }}
        </h2>
        <div
          class="promotion-details-header__description text-sm text-[#5c6378]"
          v-html="promotion.description"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DateService from "@/services/date.service";
import PromotionHexIcon from "@/views/dealer-promotions/_components/PromotionHexIcon.vue";
import PromotionProgress from "@/views/dealer-promotions/_components/PromotionProgress.vue";

export default {
  name: "PromotionDetailsHeader",

  components: {
    PromotionHexIcon,
    PromotionProgress,
  },

  props: {
    promotion: {
      type: Object,
      required: true,
    },

    showDescription: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    ...mapGetters("user", ["isUserDealer"]),

    icons: () => ({
      Gift: require("@/components/_bit/IconSvg/icons/Gift.svg"),
      Event: require("@material-symbols/svg-400/rounded/event-fill.svg"),
      Image: require("@/components/_bit/IconSvg/icons/Image.svg"),
    }),

    status() {
      return this.promotion.status?.toLowerCase?.() || this.promotion.status;
    },

    isActive() {
      return this.status === "active";
    },

    isFinished() {
      return this.status === "completed" || this.status === "completed_early";
    },

    progressData() {
      const progress = this.promotion.progress || {};
      const level = progress.level ?? 1;

      return {
        shipmentsSum: progress.shipmentsSum ?? 0,
        levelTargetValue: progress.levelTargetValue ?? (this.promotion.targetValue ?? 0) * level,
        level,
        nextLevel: level + 1,
        rewardMultiplier: progress.rewardMultiplier ?? 0,
        hasStarted: progress.hasStarted ?? true,
      };
    },

    rewardText() {
      return this.promotion.reward || "";
    },

    progressTargetValue() {
      if (this.isUserDealer) {
        return this.progressData.levelTargetValue;
      }

      return this.promotion.targetValue ?? 0;
    },

    conditionsTeaser() {
      if (this.isUserDealer || !this.promotion.description) {
        return "";
      }

      return this.stripHtml(this.promotion.description);
    },

    multiplierLabel() {
      const multiplier = this.progressData.rewardMultiplier;

      if (multiplier === null || multiplier === undefined) {
        return "×0";
      }

      return `×${multiplier}`;
    },

    dateLabel() {
      const startsAt = this.promotion.startsAt;
      const endsAt = this.promotion.endsAt;

      if (startsAt && endsAt) {
        return this.$t("dealerPromotions.period", {
          from: DateService.dateConverter(startsAt),
          to: DateService.dateConverter(endsAt),
        });
      }

      if (endsAt) {
        return this.$t("dealerPromotions.validUntil", {
          date: DateService.dateConverter(endsAt),
        });
      }

      return "–";
    },

    statusLabel() {
      const statusKey = this.status === "completed_early" ? "completed_early" : this.status;

      return this.$t(`dealerPromotions.status.${statusKey}`, "");
    },

    statusBadgeClass() {
      if (this.isActive) {
        return "bg-green-100 text-green-800";
      }

      if (this.isFinished) {
        return "bg-red-100 text-red-800";
      }

      return "bg-gray-100 text-gray-700";
    },

    bannerUrl() {
      return this.promotion.banner?.link || "";
    },

    iconUrl() {
      return this.promotion.icon?.link || "";
    },

    accentColor() {
      return this.promotion.color || "#3b82f6";
    },

    cardStyle() {
      return {
        "--promotion-color": this.accentColor,
        "--promotion-color-light": this.hexToRgba(this.accentColor, 0.12),
        "--promotion-color-badge": this.hexMixWithWhite(this.accentColor, 0.88),
      };
    },
  },

  methods: {
    stripHtml(html) {
      if (!html) {
        return "";
      }

      const element = document.createElement("div");

      element.innerHTML = html;

      return (element.textContent || element.innerText || "").replace(/\s+/g, " ").trim();
    },

    hexMixWithWhite(hex, whiteRatio = 0.88) {
      const normalized = hex.replace("#", "");

      if (normalized.length !== 3 && normalized.length !== 6) {
        return "#eef2ff";
      }

      const fullHex =
        normalized.length === 3
          ? normalized
              .split("")
              .map((char) => char + char)
              .join("")
          : normalized;

      const colorRatio = 1 - whiteRatio;
      const r = Math.round(parseInt(fullHex.slice(0, 2), 16) * colorRatio + 255 * whiteRatio);
      const g = Math.round(parseInt(fullHex.slice(2, 4), 16) * colorRatio + 255 * whiteRatio);
      const b = Math.round(parseInt(fullHex.slice(4, 6), 16) * colorRatio + 255 * whiteRatio);

      return `rgb(${r}, ${g}, ${b})`;
    },

    hexToRgba(hex, alpha) {
      const normalized = hex.replace("#", "");

      if (normalized.length !== 3 && normalized.length !== 6) {
        return `rgba(59, 130, 246, ${alpha})`;
      }

      const fullHex =
        normalized.length === 3
          ? normalized
              .split("")
              .map((char) => char + char)
              .join("")
          : normalized;

      const r = parseInt(fullHex.slice(0, 2), 16);
      const g = parseInt(fullHex.slice(2, 4), 16);
      const b = parseInt(fullHex.slice(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped lang="postcss">
.promotion-details-header__banner-image {
  @apply block w-full;

  img {
    @apply block h-auto w-full max-w-full;
  }
}

.promotion-details-header__description :deep(a) {
  @apply font-medium text-[#1e204d] underline underline-offset-2;
}

.promotion-details-header__description :deep(p) {
  @apply mb-2 last:mb-0;
}
</style>
